/* eslint-disable react-hooks/exhaustive-deps */

import { Button } from './Button'
import {
  useCustomer,
  useUpgradeTrial,
  usePreviewUpgradePrice,
} from 'hooks/customers'
import { ModalV2 } from './Common/ModalV2'
import { PriceIdToPriceValue } from 'lib/types/pricing-table.config'
import { useAuth } from 'util/auth'
import { IconCreditCard, IconHeartBolt } from '@tabler/icons-react'
import { useProjectStore } from 'store/useProjectStore'
import { toast } from 'react-hot-toast'
import { useState, useEffect } from 'react'

const formatPrice = (priceStr: string | undefined) => {
  if (!priceStr) {
    toast.error('Whoops! Please try upgrading via your account settings.')
    throw new Error('Price string is undefined')
  }
  const numericValue = priceStr.replace(/[^0-9.]/g, '')
  return `$${Number(numericValue).toLocaleString()}`
}

export const UpgradeTrialModal = () => {
  const {
    showUpgradeTrialModal,
    setShowUpgradeTrialModal,
    setShowPricingModal,
    setShowConfetti,
    selectedPlanId,
  } = useProjectStore()
  const auth = useAuth()
  const { data: customerData } = useCustomer({
    userId: auth.user ? auth.user.id : '',
  })
  const { mutateAsync: upgradeTrial, isLoading: loading } = useUpgradeTrial()
  const { mutateAsync: previewUpgradePrice, isLoading: previewLoading } =
    usePreviewUpgradePrice()

  const [previewData, setPreviewData] = useState<any>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)

  // Fetch preview pricing when modal opens and we have the necessary data
  useEffect(() => {
    if (showUpgradeTrialModal && customerData?.stripeSubscriptionId) {
      const fetchPreview = async () => {
        if (!customerData?.stripeSubscriptionId) {
          toast.error('Please try upgrading via your account settings.')
          setShowUpgradeTrialModal(false)
          return
        }
        try {
          setPreviewError(null)
          const previewParams = {
            subscriptionId: customerData.stripeSubscriptionId,
            ...(selectedPlanId !== null ? { priceId: selectedPlanId } : {}),
          }
          const preview = await previewUpgradePrice(previewParams)
          setPreviewData(preview)
        } catch (error) {
          setPreviewError('Unable to calculate pricing. Please try again.')
        }
      }

      fetchPreview()
    }
  }, [
    showUpgradeTrialModal,
    customerData?.stripeSubscriptionId,
    selectedPlanId,
    previewUpgradePrice,
  ])

  const handleUpgrade = async () => {
    if (customerData?.stripeSubscriptionId) {
      const priceId = selectedPlanId === null ? undefined : selectedPlanId
      await upgradeTrial({
        subscriptionId: customerData.stripeSubscriptionId,
        priceId,
      })

      toast.success('Your plan has been upgraded successfully!')
      setShowUpgradeTrialModal(false)
    }
  }

  const getPriceDisplay = () => {
    if (previewLoading) {
      return 'Calculating...'
    }

    if (previewError) {
      return previewError
    }

    if (previewData) {
      console.log('Preview data received:', previewData)
      const {
        formattedPrice,
        originalAmount,
        discountedAmount,
        totalDiscountAmount,
      } = previewData

      if (totalDiscountAmount > 0) {
        const originalFormatted = `$${(originalAmount / 100).toFixed(2)}`
        const discountedFormatted = `$${(discountedAmount / 100).toFixed(2)}`
        console.log('Showing discounted price:', {
          originalFormatted,
          discountedFormatted,
        })
        return (
          <span>
            <span className="text-gray-400 line-through">
              {originalFormatted}
            </span>{' '}
            <span className="font-semibold text-green-600">
              {discountedFormatted}
            </span>
            <span className="ml-1 text-sm text-green-600">
              (Save ${(totalDiscountAmount / 100).toFixed(2)})
            </span>
          </span>
        )
      } else {
        return `$${(discountedAmount / 100).toFixed(2)}`
      }
    }

    // Fallback to original pricing
    return formatPrice(
      PriceIdToPriceValue[customerData?.stripePriceId as string]
    )
  }

  return (
    <ModalV2
      open={showUpgradeTrialModal}
      onClose={() => setShowUpgradeTrialModal(false)}
    >
      <div className="mb-4 flex flex-col gap-y-2">
        <div className="flex items-center gap-x-2 text-lg font-semibold">
          <IconHeartBolt className="h-6 w-6 text-red-500" /> Join Outcast!
        </div>
        <p className="text-sm text-gray-500">
          Please confirm that you&apos;d like to upgrade your trial. You will be
          charged {getPriceDisplay()}.
        </p>
      </div>
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          color="white"
          onClick={() => {
            setShowUpgradeTrialModal(false)
            setShowPricingModal(true)
          }}
        >
          Compare Plans
        </Button>
        <Button
          loading={loading}
          onClick={handleUpgrade}
          icon={IconCreditCard}
          disabled={previewLoading || !!previewError}
        >
          Upgrade My Plan
        </Button>
      </div>
    </ModalV2>
  )
}
